import { useState } from 'react';
import { X, ExternalLink } from 'lucide-react';

interface Props {
  suggestedUrl: string;
  artistName: string | null;
  releaseName: string | null;
  message?: string | null;
  onConfirm: (url: string) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Lightweight verification dialog shown when the
 * `add-creative-to-meta-campaign` edge function returns a 409 with a
 * song.so suggestion. The user may accept the suggested URL as-is, edit
 * it, or cancel. Replaces the native `window.prompt` flow that didn't
 * match the rest of the app's modal styling.
 */
export function ConfirmSmartlinkModal({
  suggestedUrl,
  artistName,
  releaseName,
  message,
  onConfirm,
  onCancel,
}: Props) {
  const [value, setValue] = useState(suggestedUrl);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onConfirm(trimmed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold dark:text-white flex items-center gap-2">
            <span className="w-5 h-5 rounded-sm bg-black flex items-center justify-center text-white text-[10px] font-bold">s.</span>
            Confirm song.so smartlink
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No landing URL is stored for this campaign yet. song.so suggested the following URL for{' '}
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {artistName || '?'} — {releaseName || '?'}
            </span>
            . Verify and confirm before the Meta ad is created.
          </p>

          {message && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 italic">{message}</p>
          )}

          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            Smartlink URL
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={submitting}
            className="w-full px-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConfirm();
              if (e.key === 'Escape') onCancel();
            }}
          />

          {value.trim() && (
            <a
              href={value.trim()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              Open in new tab
            </a>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-b-lg">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-sm rounded text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !value.trim()}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {submitting ? 'Confirming…' : 'Use this URL'}
          </button>
        </div>
      </div>
    </div>
  );
}
